import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/estrategia' },
  { hreflang: 'en', href: '/en/strategy' },
  { hreflang: 'x-default', href: '/estrategia' },
];

const TEXT = {
  es: {
    seoTitle: 'Cómo innovamos | Estrategia de innovación | Valoriza',
    seoDescription: 'Cuatro áreas de foco, cuatro vehículos de ejecución y un criterio claro de qué es innovación en Valoriza para 2026.',
    jsonLdName: 'Cómo innovamos en Valoriza',
    breadcrumb: [{ label: 'Inicio', to: '/' }, { label: 'Estrategia' }],
    tag: 'Estrategia 2026',
    h1a: 'Cómo',
    h1b: 'innovamos',
    lead: 'Cuatro áreas de foco, cuatro vehículos de ejecución y un criterio claro: qué es innovación, qué es IT y qué es mejora continua.',
    focusTitle: 'Áreas de foco 2026',
    vehiclesTitle: 'Vehículos de ejecución',
    highlightsTitle: 'Highlights 2025',
    focusAreas: [
      { code: 'A1', name: 'Nuevas cadenas de valor para el residuo', desc: 'Convertir el residuo en recurso. Productos y mercados para los outputs de planta. Posicionarse en flujos emergentes (baterías, fotovoltaica, palas) y en retos energéticos (CO₂, biometano).', claim: 'De coste a ingreso. Nuevas líneas. Liderazgo en circularidad.' },
      { code: 'A2', name: 'Tecnología para operación y seguridad', desc: 'Proteger a la gente con tecnología. Reducir riesgos físicos críticos (pinchazos, caídas, gases, seguridad vial). Flota sensorizada y gestor común.', claim: 'Menos accidentes. Diferencial en licitación. Eficiencia transversal.' },
      { code: 'A3', name: 'Cultura y personas: el motor', desc: 'Cerrar la brecha digital de +12.000 operarios. Sin adopción, no hay innovación. Cultura digital para que la organización esté lista.', claim: 'Base de cualquier adopción. Upskilling masivo.' },
      { code: 'A4', name: 'Solidez y madurez de la función de innovación', desc: 'Inteligencia de mercado sistemática. Anticipar al cliente. Función de innovación visible y escalable.', claim: 'Sistema de gestión de la innovación en implantación, alineado con ISO 56001.' },
    ],
    vehicles: [
      { code: 'V1', name: 'Scouting', desc: 'Identificación temprana de tendencias y tecnologías emergentes.' },
      { code: 'V2', name: 'Pilotos y PoC', desc: 'Validación de conceptos y viabilidad técnica y operativa.' },
      { code: 'V3', name: 'Fondos públicos / UE', desc: 'Captación de financiación para proyectos de I+D+i ambiciosos (CDTI, NextGenerationEU, LIFE).' },
      { code: 'V4', name: 'Open Innovation', desc: 'Colaboración con startups, centros tecnológicos y ecosistemas (Patio Campus, Area101, CIRCE, Tecnalia, Shakers).' },
    ],
    highlights: [{ v: '19', l: 'Proyectos activos' }, { v: '10', l: 'Nuevos' }, { v: '8', l: 'De I+D' }, { v: '+3 M€', l: 'Inversión en innovación operativa' }],
    highlightsBody: 'La madurez de nuestra función de innovación se refleja en la implantación de nuestro sistema de gestión, alineado con la norma ISO 56001, asegurando un enfoque sistemático y repetible para la generación de valor. El programa Shakers reconoce a las personas que impulsan esta innovación.',
    linksTitle: 'Enlaces',
    linkProjects: 'Nuestros proyectos I+D+i',
    linkPatents: 'Nuestra propiedad industrial',
    linkChallenges: 'Nuestros retos de innovación',
    linkTeam: 'El equipo de innovación',
    projectsPath: '/proyectos',
    patentsPath: '/patentes',
    challengesPath: '/retos',
    teamPath: '/equipo',
  },
  en: {
    seoTitle: 'How we innovate | Innovation strategy | Valoriza',
    seoDescription: 'Four focus areas, four execution vehicles and a clear criterion for what counts as innovation at Valoriza for 2026.',
    jsonLdName: 'How Valoriza innovates',
    breadcrumb: [{ label: 'Home', to: '/en' }, { label: 'Strategy' }],
    tag: '2026 Strategy',
    h1a: 'How we',
    h1b: 'innovate',
    lead: 'Four focus areas, four execution vehicles and a clear criterion: what is innovation, what is IT, and what is continuous improvement.',
    focusTitle: '2026 Focus areas',
    vehiclesTitle: 'Execution vehicles',
    highlightsTitle: '2025 Highlights',
    focusAreas: [
      { code: 'A1', name: 'New value chains for waste', desc: 'Turning waste into a resource. Products and markets for plant outputs. Positioning in emerging flows (batteries, photovoltaics, blades) and energy challenges (CO₂, biomethane).', claim: 'From cost to revenue. New lines. Circularity leadership.' },
      { code: 'A2', name: 'Technology for operations and safety', desc: 'Protecting people with technology. Reducing critical physical risks (punctures, falls, gases, road safety). Sensorized fleet and a shared manager.', claim: 'Fewer accidents. A differentiator in tenders. Cross-cutting efficiency.' },
      { code: 'A3', name: 'Culture and people: the engine', desc: 'Closing the digital gap for +12,000 field workers. Without adoption, there’s no innovation. Digital culture so the organization is ready.', claim: 'The foundation of any adoption. Mass upskilling.' },
      { code: 'A4', name: 'Solidity and maturity of the innovation function', desc: 'Systematic market intelligence. Anticipating the client. A visible, scalable innovation function.', claim: 'Innovation management system underway, aligned with ISO 56001.' },
    ],
    vehicles: [
      { code: 'V1', name: 'Scouting', desc: 'Early identification of emerging trends and technologies.' },
      { code: 'V2', name: 'Pilots & PoCs', desc: 'Validating concepts and technical/operational feasibility.' },
      { code: 'V3', name: 'Public / EU funds', desc: 'Securing funding for ambitious R&D projects (CDTI, NextGenerationEU, LIFE).' },
      { code: 'V4', name: 'Open Innovation', desc: 'Collaboration with startups, tech centers and ecosystems (Patio Campus, Area101, CIRCE, Tecnalia, Shakers).' },
    ],
    highlights: [{ v: '19', l: 'Active projects' }, { v: '10', l: 'New' }, { v: '8', l: 'R&D' }, { v: '+€3M', l: 'Investment in operational innovation' }],
    highlightsBody: 'The maturity of our innovation function is reflected in the rollout of our management system, aligned with the ISO 56001 standard, ensuring a systematic and repeatable approach to value creation. The Shakers program recognizes the people driving this innovation.',
    linksTitle: 'Links',
    linkProjects: 'Our R&D projects',
    linkPatents: 'Our industrial property',
    linkChallenges: 'Our innovation challenges',
    linkTeam: 'The innovation team',
    projectsPath: '/en/projects',
    patentsPath: '/patentes',
    challengesPath: '/en/challenges',
    teamPath: '/en/team',
  },
};

export default function EstrategiaPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const t = TEXT[locale];
  const path = locale === 'en' ? '/en/strategy' : '/estrategia';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t.jsonLdName,
    url: `https://innovation.valorizasm.com${path}`,
  };

  return (
    <>
      <SEOHead
        title={t.seoTitle}
        description={t.seoDescription}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        jsonLd={[jsonLd]}
      />
      <div className="pt-32 pb-24 v-container" data-testid="estrategia-page">
        <Breadcrumb items={t.breadcrumb} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mb-14">
          <span className="v-tag green mb-6 inline-flex">{t.tag}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-6 tracking-tight leading-tight" data-testid="estrategia-h1">
            {t.h1a} <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>{t.h1b}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 leading-relaxed">{t.lead}</p>
        </motion.div>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-black dark:text-white/90 mb-6">{t.focusTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {t.focusAreas.map((a) => (
              <div key={a.code} className="liquid-glass rounded-2xl p-6" data-testid={`focus-area-${a.code}`}>
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full inline-flex mb-3" style={{ background: 'rgba(141,194,31,0.12)', color: '#8DC21F' }}>{a.code}</span>
                <h3 className="text-base font-bold text-black dark:text-white/90 mb-2">{a.name}</h3>
                <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed mb-3">{a.desc}</p>
                <p className="text-xs font-semibold" style={{ color: '#8DC21F' }}>{a.claim}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-black dark:text-white/90 mb-6">{t.vehiclesTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.vehicles.map((v) => (
              <div key={v.code} className="glass-card p-5">
                <span className="text-xs font-bold" style={{ color: '#8DC21F' }}>{v.code}</span>
                <h3 className="text-sm font-bold text-black dark:text-white/90 mt-1 mb-2">{v.name}</h3>
                <p className="text-xs text-gray-700 dark:text-white/60 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14 max-w-3xl">
          <h2 className="text-xl font-bold text-black dark:text-white/90 mb-4">{t.highlightsTitle}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            {t.highlights.map((s) => (
              <div key={s.l} className="glass-card p-4 text-center">
                <div className="text-lg font-bold" style={{ color: '#8DC21F' }}>{s.v}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-white/50">{s.l}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed">{t.highlightsBody}</p>
        </section>

        <div className="glass-card p-6 max-w-2xl mb-10">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{t.linksTitle}</h3>
          <div className="flex flex-col gap-2.5 text-sm">
            <Link to={t.projectsPath} className="hover:text-[#8DC21F] transition-colors">{t.linkProjects}</Link>
            <Link to={t.patentsPath} className="hover:text-[#8DC21F] transition-colors">{t.linkPatents}</Link>
            <Link to={t.challengesPath} className="hover:text-[#8DC21F] transition-colors">{t.linkChallenges}</Link>
            <Link to={t.teamPath} className="hover:text-[#8DC21F] transition-colors">{t.linkTeam}</Link>
          </div>
        </div>
      </div>
    </>
  );
}
